import React, { useState, useEffect, useRef } from 'react';
import { X, Play, Plus, Volume2, VolumeX } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import {
  Dialog,
  DialogContent,
  DialogHeader,
} from './ui/dialog';
import Hls from 'hls.js';
import LikeButton from './LikeButton';
import ShareButton from './ShareButton';

const MovieModal = ({ movie, isOpen, onClose, onPlay, onAddToList, onOpenShare }) => {
  const [isMuted, setIsMuted] = useState(true);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef(null);
  const hlsRef = useRef(null);

  useEffect(() => {
    if (!isOpen || !movie || !videoRef.current) return;

    // Initialize HLS for video streaming
    if (movie.streamUrl && movie.contentType === 'video') {
      const video = videoRef.current;
      
      if (Hls.isSupported()) {
        const hls = new Hls({
          debug: false,
          enableWorker: true,
          lowLatencyMode: true,
          backBufferLength: 90
        });
        
        hls.loadSource(movie.streamUrl);
        hls.attachMedia(video);
        
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          console.log('HLS manifest loaded, ready to play');
        });
        
        hls.on(Hls.Events.ERROR, (event, data) => {
          console.error('HLS error:', data);
          if (data.fatal) {
            switch (data.type) {
              case Hls.ErrorTypes.NETWORK_ERROR:
                console.log('Fatal network error, trying to recover');
                hls.startLoad();
                break;
              case Hls.ErrorTypes.MEDIA_ERROR:
                console.log('Fatal media error, trying to recover');
                hls.recoverMediaError();
                break;
              default:
                console.log('Fatal error, cannot recover');
                hls.destroy();
                break;
            }
          }
        });
        
        hlsRef.current = hls;
      } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
        // Native HLS support (Safari)
        video.src = movie.streamUrl;
      }
    }

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [isOpen, movie]);

  const handlePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  if (!movie) return null;

  const similarMovies = [
    { id: 101, title: "Similar Movie 1", poster: "https://image.tmdb.org/t/p/w300/poster1.jpg", rating: "TV-14", duration: "45m" },
    { id: 102, title: "Similar Movie 2", poster: "https://image.tmdb.org/t/p/w300/poster2.jpg", rating: "TV-MA", duration: "52m" },
    { id: 103, title: "Similar Movie 3", poster: "https://image.tmdb.org/t/p/w300/poster3.jpg", rating: "TV-14", duration: "48m" }
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-gray-900 text-white border-gray-700 p-0">
        <div className="relative">
          {/* Video/Image Section */}
          <div className="relative aspect-video bg-black">
            {movie.streamUrl && movie.contentType === 'video' ? (
              <div className="w-full h-full relative">
                <video
                  ref={videoRef}
                  className="w-full h-full"
                  controls
                  autoPlay
                  muted={isMuted}
                  playsInline
                  onPlay={() => setIsPlaying(true)}
                  onPause={() => setIsPlaying(false)}
                >
                  Your browser does not support video playback.
                </video>
                
                {/* Custom Controls Overlay */}
                <div className="absolute bottom-4 right-4 flex gap-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={toggleMute}
                    className="bg-black/60 hover:bg-black/80"
                  >
                    {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            ) : movie.videoId ? (
              <div className="w-full h-full">
                <iframe
                  src={`https://www.youtube.com/embed/${movie.videoId}?autoplay=1&mute=${isMuted ? 1 : 0}&controls=1&showinfo=0&rel=0&modestbranding=1&fs=1&cc_load_policy=1&iv_load_policy=3&start=0&enablejsapi=1&origin=${window.location.origin}&playsinline=1`}
                  className="w-full h-full"
                  frameBorder="0"
                  allow="autoplay; encrypted-media; fullscreen; accelerometer; gyroscope; picture-in-picture"
                  allowFullScreen
                  title={movie.title}
                  loading="lazy"
                  onError={() => {
                    console.log('Video failed to load, showing backdrop');
                  }}
                />
              </div>
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gray-900">
                <img
                  src={movie.backdrop}
                  alt={movie.title}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = 'https://via.placeholder.com/1920x1080/1f2937/9ca3af?text=' + encodeURIComponent(movie.title);
                  }}
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="bg-black/80 text-white px-4 py-2 rounded-lg">
                    <p className="text-center">Video Currently Unavailable</p>
                    <p className="text-sm text-gray-300">Please try again later</p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Gradient overlay */}
            <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-gray-900 to-transparent" />
            
            {/* Controls */}
            <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between">
              <div className="flex items-center gap-4">
                <Button
                  size="lg"
                  onClick={() => onPlay(movie)}
                  className="bg-white text-black hover:bg-gray-200 font-semibold"
                >
                  <Play className="w-6 h-6 mr-2 fill-current" />
                  Play
                </Button>
                
                <Button
                  variant="ghost"
                  size="lg"
                  onClick={() => onAddToList(movie)}
                  className="text-white hover:text-white border border-gray-400 hover:border-white rounded-full w-12 h-12 p-0"
                >
                  <Plus className="w-6 h-6" />
                </Button>
                
                <LikeButton 
                  contentId={movie.id} 
                  initialLiked={movie.isLiked}
                  initialCount={movie.like_count || 0}
                  size="lg"
                  showCount={true}
                />
                
                <ShareButton 
                  content={movie}
                  size="lg"
                  showCount={true}
                  onOpenShareModal={onOpenShare}
                />
              </div>
              
              {movie.videoId && (
                <Button
                  variant="ghost"
                  size="lg"
                  onClick={() => setIsMuted(!isMuted)}
                  className="text-white hover:text-white border border-gray-400 hover:border-white rounded-full w-12 h-12 p-0"
                >
                  {isMuted ? <VolumeX className="w-6 h-6" /> : <Volume2 className="w-6 h-6" />}
                </Button>
              )}
            </div>

            {/* Close button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="absolute top-4 right-4 text-white hover:text-white bg-gray-800/80 hover:bg-gray-700 rounded-full w-10 h-10 p-0"
            >
              <X className="w-6 h-6" />
            </Button>
          </div>

          {/* Movie Info Section */}
          <div className="p-6">
            <div className="grid md:grid-cols-3 gap-8">
              {/* Left Column - Main Info */}
              <div className="md:col-span-2">
                <div className="flex items-center gap-2 mb-4">
                  <Badge variant="outline" className="text-green-400 border-green-400">
                    98% Match
                  </Badge>
                  <span className="text-white">{movie.year}</span>
                  <span className="text-gray-400">{movie.rating}</span>
                  <span className="text-gray-400">{movie.duration}</span>
                  {movie.isNetflixOriginal && (
                    <Badge className="bg-black/80 text-white border-0 flex items-center">
                      <span className="text-red-500">I</span>
                      <span className="text-orange-500">G</span>
                      <span className="text-yellow-500">o</span>
                      <span className="text-green-500">t</span>
                      <span className="text-blue-500 ml-1">S</span>
                      <span className="text-indigo-500">o</span>
                      <span className="text-purple-500">u</span>
                      <span className="text-pink-500">l</span>
                    </Badge>
                  )}
                </div>

                <h1 className="text-3xl font-bold text-white mb-4">{movie.title}</h1>
                
                <p className="text-gray-300 mb-4 leading-relaxed">
                  {showFullDescription ? movie.description : `${movie.description.slice(0, 200)}...`}
                  {movie.description.length > 200 && (
                    <button 
                      onClick={() => setShowFullDescription(!showFullDescription)}
                      className="text-white hover:underline ml-2"
                    >
                      {showFullDescription ? 'Show less' : 'Show more'}
                    </button>
                  )}
                </p>
              </div>

              {/* Right Column - Cast & Details */}
              <div className="space-y-4">
                <div>
                  <span className="text-gray-400">Cast: </span>
                  <span className="text-gray-300">
                    Millie Bobby Brown, Finn Wolfhard, David Harbour, Winona Ryder
                  </span>
                </div>
                
                <div>
                  <span className="text-gray-400">Genres: </span>
                  <span className="text-gray-300">{movie.genre}, Thriller, Supernatural</span>
                </div>
                
                <div>
                  <span className="text-gray-400">This show is: </span>
                  <span className="text-gray-300">Suspenseful, Dark, Mind-bending</span>
                </div>
              </div>
            </div>

            <Separator className="bg-gray-700 my-8" />

            {/* More Like This Section */}
            <div>
              <h3 className="text-xl font-semibold text-white mb-6">More Like This</h3>
              <div className="grid grid-cols-3 gap-4">
                {similarMovies.map((similar) => (
                  <div key={similar.id} className="bg-gray-800 rounded-lg overflow-hidden">
                    <div className="aspect-video bg-gray-700">
                      <img
                        src={similar.poster}
                        alt={similar.title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/300x450/374151/9CA3AF?text=' + encodeURIComponent(similar.title);
                        }}
                      />
                    </div>
                    <div className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <Badge variant="outline" className="text-green-400 border-green-400 text-xs">
                          95% Match
                        </Badge>
                        <span className="text-xs text-gray-400">{similar.duration}</span>
                      </div>
                      <h4 className="text-white font-medium text-sm mb-1">{similar.title}</h4>
                      <p className="text-gray-400 text-xs">Action • Thriller</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MovieModal;